import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { DashboardLayout } from '../components/DashboardLayout';
import { useAuth } from '../context/AuthContext';
import { usePWA } from '../context/PWAContext';
import { useCurrency } from '../context/CurrencyContext';
import { api } from '../services/api';
import { 
  User, Mail, Calendar, Crown, Briefcase, ArrowRight, 
  CreditCard, Receipt, AlertTriangle, Loader2, CheckCircle,
  XCircle, Clock, ChevronDown, ChevronUp, Bell, BellOff, Share2,
  Coins, FileText, Cookie, AlertCircle, Scale, HelpCircle, Copy, Wallet,
  Banknote, ExternalLink, ShieldCheck
} from 'lucide-react';
import { formatDate } from '../utils/helpers';
import { Button } from '../components/ui/button';
import { FEATURES } from '../config/features';
import { PersonalProfileSection } from '../components/PersonalProfileSection';
import { PrivacyGdprSection } from '../components/PrivacyGdprSection';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../components/ui/dialog';
import SecuritySettings from '../components/SecuritySettings';

export default function Settings() {
  const { user, refreshUser } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const pwaContext = usePWA();
  const { notificationPermission, requestNotificationPermission, shareContent } = pwaContext || {};
  const { currency, currencies, currencyConfig, updateCurrency, formatCurrency, loading: currencyLoading, exchangeRates } = useCurrency();
  const [usage, setUsage] = useState(null);
  const [subscriptionDetails, setSubscriptionDetails] = useState(null);
  const [billingHistory, setBillingHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showBillingHistory, setShowBillingHistory] = useState(false);
  const [cancelDialog, setCancelDialog] = useState(false);
  const [downgradeDialog, setDowngradeDialog] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [showCurrencyDropdown, setShowCurrencyDropdown] = useState(false);
  const [connectStatus, setConnectStatus] = useState(null);
  const [connectLoading, setConnectLoading] = useState(false);

  // Handle Stripe Connect return
  useEffect(() => {
    const connect = searchParams.get('connect');
    if (connect === 'success') {
      toast.success('Stripe onboarding updated. Checking status...');
      searchParams.delete('connect');
      setSearchParams(searchParams, { replace: true });
      fetchConnectStatus();
    } else if (connect === 'refresh') {
      toast.info('Let us refresh your onboarding link...');
      handleConnectOnboarding();
      searchParams.delete('connect');
      setSearchParams(searchParams, { replace: true });
    }
  }, []);

  useEffect(() => {
    fetchConnectStatus();
  }, []);

  const fetchConnectStatus = async () => {
    try {
      const res = await api.getConnectStatus();
      setConnectStatus(res.data);
    } catch (err) {
      console.error('Connect status error', err);
    }
  };

  const handleConnectOnboarding = async () => {
    setConnectLoading(true);
    try {
      const res = connectStatus?.connected ? await api.refreshConnectLink() : await api.createConnectAccount();
      if (res.data?.url) {
        window.location.href = res.data.url;
      }
    } catch (err) {
      const detail = err.response?.data?.detail;
      // Structured "platform profile incomplete" error from backend
      if (detail && typeof detail === 'object' && detail.code === 'stripe_platform_profile_incomplete') {
        toast.error(detail.message, {
          duration: 20000,
          action: {
            label: detail.action_label || 'Open Stripe',
            onClick: () => window.open(detail.action_url, '_blank'),
          },
        });
      } else {
        const msg = (typeof detail === 'string' ? detail : null) || 'Unable to start Stripe onboarding';
        toast.error(msg, { duration: 12000 });
      }
    } finally {
      setConnectLoading(false);
    }
  };

  const handleOpenDashboard = async () => {
    setConnectLoading(true);
    try {
      const res = await api.connectLoginLink();
      if (res.data?.url) window.open(res.data.url, '_blank');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Unable to open Stripe dashboard');
    } finally {
      setConnectLoading(false);
    }
  };

  // Handle Digital Twin payment callback
  useEffect(() => {
    const dtPayment = searchParams.get('dt_payment');
    if (dtPayment === 'success') {
      // Auto-activate after successful Stripe payment
      api.activateDigitalTwinAccess().then(() => {
        toast.success('Digital Twin Vault activated! You now have full access.');
        refreshUser();
      }).catch(() => {});
      searchParams.delete('dt_payment');
      setSearchParams(searchParams, { replace: true });
    } else if (dtPayment === 'cancelled') {
      toast.error('Payment was cancelled. You can try again anytime.');
      searchParams.delete('dt_payment');
      setSearchParams(searchParams, { replace: true });
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [usageRes, detailsRes, historyRes] = await Promise.all([
        api.getSubscriptionUsage(),
        api.getSubscriptionDetails(),
        api.getBillingHistory(10)
      ]);
      setUsage(usageRes.data);
      setSubscriptionDetails(detailsRes.data);
      setBillingHistory(historyRes.data.transactions || []);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCancelSubscription = async () => {
    setProcessing(true);
    try {
      const res = await api.cancelSubscription();
      const msg = res?.data?.message || 'Membership cancelled.';
      toast.success(msg);
      setCancelDialog(false);
      if (refreshUser) await refreshUser();
      await fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to cancel membership');
    } finally {
      setProcessing(false);
    }
  };

  const handleReactivateSubscription = async () => {
    setProcessing(true);
    try {
      const res = await api.reactivateSubscription();
      toast.success(res?.data?.message || 'Membership reactivated.');
      if (refreshUser) await refreshUser();
      await fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reactivate membership');
    } finally {
      setProcessing(false);
    }
  };

  const handleDowngrade = async () => {
    setProcessing(true);
    try {
      await api.downgradeSubscription();
      toast.success('Membership downgraded to Professional');
      setDowngradeDialog(false);
      if (refreshUser) await refreshUser();
      await fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to downgrade membership');
    } finally {
      setProcessing(false);
    }
  };

  const getPlanColor = (tier) => {
    switch (tier) {
      case 'elite': return 'text-purple-400';
      case 'professional': return 'text-blue-400';
      default: return 'text-gold';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'paid':
        return <CheckCircle size={16} className="text-green-500" />;
      case 'pending':
        return <Clock size={16} className="text-yellow-500" />;
      default:
        return <XCircle size={16} className="text-red-500" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'paid': return 'text-green-500';
      case 'pending': return 'text-yellow-500';
      default: return 'text-red-500';
    }
  };

  return (
    <DashboardLayout>
      <div className="p-6 lg:p-8 max-w-4xl mx-auto" data-testid="settings-page">
        {/* Header */}
        <div className="mb-8">
          <p className="text-gold uppercase tracking-[0.2em] text-xs mb-2">Account</p>
          <h1 className="font-serif text-3xl text-white">Settings</h1>
        </div>

        {/* Profile Card */}
        <div className="luxury-card p-8 mb-6">
          <h2 className="font-serif text-xl text-white mb-6">Profile Information</h2>
          
          <div className="space-y-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-gold/20 border border-gold/30 flex items-center justify-center">
                <User size={24} className="text-gold" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Full Name</p>
                <p className="text-white text-lg">{user?.full_name}</p>
              </div>
            </div>

            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-[#1A1A1A] border border-[#262626] flex items-center justify-center">
                <Mail size={24} className="text-muted-foreground" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Email Address</p>
                <p className="text-white text-lg">{user?.email}</p>
              </div>
            </div>

            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-[#1A1A1A] border border-[#262626] flex items-center justify-center">
                <Calendar size={24} className="text-muted-foreground" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Member Since</p>
                <p className="text-white text-lg">{formatDate(user?.created_at)}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Investor Certification Card - hidden when tokenisation disabled */}
        {FEATURES.TOKENISATION_ENABLED && (
        <div className="luxury-card p-8 mb-6" data-testid="investor-cert-card">
          <div className="flex items-center gap-3 mb-6">
            <ShieldCheck size={24} className="text-gold" />
            <h2 className="font-serif text-xl text-white">Investor Certification</h2>
          </div>
          {user?.kyc_tier && user.kyc_tier !== 'none' && user?.kyc_expires_at && new Date(user.kyc_expires_at) > new Date() ? (
            <div className="space-y-3">
              <div className="flex items-start justify-between flex-wrap gap-3">
                <div>
                  <p className="text-white font-serif text-lg">
                    {user.kyc_tier === 'hnwi' ? 'Certified High Net Worth Individual' : 'Certified Sophisticated Investor'}
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    Certified {user.kyc_certified_at && new Date(user.kyc_certified_at).toLocaleDateString('en-GB')}
                    {' · '}Valid until {new Date(user.kyc_expires_at).toLocaleDateString('en-GB')}
                  </p>
                </div>
                <Link to="/onboarding/investor-certification">
                  <Button variant="outline" className="border-gold/30 text-gold hover:bg-gold/10" data-testid="view-cert-btn">
                    View / revoke
                  </Button>
                </Link>
              </div>
              <p className="text-[11px] text-muted-foreground italic leading-relaxed">
                Your certification covers UK FCA Financial Promotion Order exemptions for non-readily realisable securities. Required for tokenisation and marketplace purchases.
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              <p className="text-sm text-muted-foreground leading-relaxed">
                Tokenisation and marketplace purchases require self-certification as a High Net Worth or Sophisticated Investor under UK FCA rules. Takes about 2 minutes.
              </p>
              <Link to="/onboarding/investor-certification">
                <Button className="btn-primary flex items-center gap-2" data-testid="start-cert-btn">
                  <ShieldCheck size={14} /> Start certification
                </Button>
              </Link>
            </div>
          )}
        </div>
        )}

        {/* Personal Profile (encrypted) */}
        <PersonalProfileSection />

        {/* Privacy & GDPR */}
        <PrivacyGdprSection />

        {/* Membership Card */}
        <div className="luxury-card p-8 mb-6">
          <div className="flex items-center gap-3 mb-6">
            <Crown size={24} className="text-gold" />
            <h2 className="font-serif text-xl text-white">Membership</h2>
          </div>
          
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <div className="w-6 h-6 border-2 border-gold border-t-transparent rounded-full animate-spin" />
            </div>
          ) : usage && subscriptionDetails ? (
            <>
              {/* Current Plan Overview */}
              <div className="p-6 bg-gradient-to-br from-gold/10 to-transparent border border-gold/20 mb-6">
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-4">
                  <div>
                    <p className="text-xs text-gold uppercase tracking-wider mb-2">Current Plan</p>
                    <p className={`text-2xl font-serif ${getPlanColor(usage.tier)}`}>
                      {usage.plan_name}
                    </p>
                    {subscriptionDetails.billing_cycle && usage.tier !== 'free' && (
                      <p className="text-sm text-muted-foreground mt-1">
                        {subscriptionDetails.price_display} • {subscriptionDetails.billing_cycle === 'yearly' ? 'Annual' : 'Monthly'} billing
                      </p>
                    )}
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-muted-foreground uppercase tracking-wider mb-2">Assets</p>
                    <p className="text-2xl text-white">
                      {usage.asset_count} / {usage.asset_limit === 'unlimited' ? '∞' : usage.asset_limit}
                    </p>
                  </div>
                </div>
                
                {/* Usage Bar */}
                {usage.asset_limit !== 'unlimited' && (
                  <div>
                    <div className="h-2 bg-[#1A1A1A] rounded-full overflow-hidden mb-2">
                      <div 
                        className={`h-full transition-all ${
                          usage.usage_percentage > 80 ? 'bg-red-500' : 'bg-gold'
                        }`}
                        style={{ width: `${Math.min(usage.usage_percentage, 100)}%` }}
                      />
                    </div>
                    <div className="flex justify-between text-xs text-muted-foreground">
                      <span>{usage.usage_percentage}% used</span>
                      <span>
                        {usage.assets_remaining === 'unlimited' 
                          ? 'Unlimited remaining' 
                          : `${usage.assets_remaining} remaining`}
                      </span>
                    </div>
                  </div>
                )}

                {/* Next Billing Date */}
                {subscriptionDetails.next_billing_date && (
                  <div className="mt-4 pt-4 border-t border-gold/20">
                    <p className="text-xs text-muted-foreground">
                      Next billing: {formatDate(subscriptionDetails.next_billing_date)}
                    </p>
                  </div>
                )}
              </div>

              {/* Asset Usage Details */}
              <div className="flex items-center gap-4 p-4 bg-[#0A0A0A] border border-[#1A1A1A] mb-6">
                <Briefcase size={20} className="text-gold" />
                <div className="flex-1">
                  <p className="text-sm text-white">Asset Slots</p>
                  <p className="text-xs text-muted-foreground">
                    {usage.can_add_assets 
                      ? `You can add ${usage.assets_remaining === 'unlimited' ? 'unlimited' : usage.assets_remaining} more assets`
                      : 'You have reached your asset limit'}
                  </p>
                </div>
                {!usage.can_add_assets && (
                  <span className="px-2 py-1 text-xs bg-red-500/20 text-red-400">Limit Reached</span>
                )}
              </div>

              {/* Pending cancellation banner */}
              {subscriptionDetails.cancel_at_period_end && (
                <div className="mb-4 p-4 border border-amber-500/30 bg-amber-500/10 rounded">
                  <p className="text-sm text-amber-300" data-testid="pending-cancellation-banner">
                    Your membership is set to cancel on{' '}
                    <strong>
                      {subscriptionDetails.next_billing_date
                        ? new Date(subscriptionDetails.next_billing_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })
                        : 'your next billing date'}
                    </strong>
                    . You'll keep full access until then.
                  </p>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex flex-col sm:flex-row gap-3 mb-6">
                {/* Upgrade Button */}
                {subscriptionDetails.can_upgrade && (
                  <Link to="/pricing" className="flex-1">
                    <Button className="btn-primary w-full flex items-center justify-center gap-2" data-testid="upgrade-btn">
                      <CreditCard size={16} />
                      {usage.tier === 'free' ? 'Upgrade to Professional' : 'Upgrade to Elite'}
                    </Button>
                  </Link>
                )}

                {/* Downgrade Button (Elite only) */}
                {usage.tier === 'elite' && (
                  <Button 
                    onClick={() => setDowngradeDialog(true)}
                    className="flex-1 bg-[#1A1A1A] hover:bg-[#262626] text-white"
                    data-testid="downgrade-btn"
                  >
                    Downgrade to Professional
                  </Button>
                )}

                {/* Reactivate Button (only when cancellation is pending) */}
                {subscriptionDetails.can_reactivate && (
                  <Button 
                    onClick={handleReactivateSubscription}
                    disabled={processing}
                    className="flex-1 bg-gold text-black hover:bg-gold/90"
                    data-testid="reactivate-membership-btn"
                  >
                    {processing ? 'Working...' : 'Keep My Membership'}
                  </Button>
                )}

                {/* Cancel Button (Paid plans only, not already pending cancel) */}
                {subscriptionDetails.can_cancel && (
                  <Button 
                    onClick={() => setCancelDialog(true)}
                    variant="outline"
                    className="flex-1 border-red-500/30 text-red-400 hover:bg-red-500/10"
                    data-testid="cancel-membership-btn"
                  >
                    Cancel Membership
                  </Button>
                )}
              </div>

              {usage.tier === 'elite' && (
                <div className="text-center py-2 mb-6">
                  <p className="text-sm text-muted-foreground">
                    You have access to all Assagart features.
                  </p>
                </div>
              )}

              {/* View Plans Link */}
              <div className="pt-4 border-t border-[#1A1A1A]">
                <Link 
                  to="/pricing" 
                  className="text-gold text-sm hover:underline flex items-center gap-1"
                  data-testid="view-all-plans"
                >
                  View all plans <ArrowRight size={14} />
                </Link>
              </div>
            </>
          ) : (
            <p className="text-muted-foreground text-center py-4">Unable to load membership info</p>
          )}
        </div>

        {/* Billing History Card */}
        <div className="luxury-card p-8">
          <button 
            onClick={() => setShowBillingHistory(!showBillingHistory)}
            className="w-full flex items-center justify-between"
            data-testid="billing-history-toggle"
          >
            <div className="flex items-center gap-3">
              <Receipt size={24} className="text-gold" />
              <h2 className="font-serif text-xl text-white">Billing History</h2>
            </div>
            {showBillingHistory ? (
              <ChevronUp size={20} className="text-muted-foreground" />
            ) : (
              <ChevronDown size={20} className="text-muted-foreground" />
            )}
          </button>

          {showBillingHistory && (
            <div className="mt-6">
              {billingHistory.length > 0 ? (
                <div className="space-y-3">
                  {billingHistory.map((tx) => (
                    <div 
                      key={tx.id || tx.session_id}
                      className="flex items-center justify-between p-4 bg-[#0A0A0A] border border-[#1A1A1A]"
                      data-testid={`billing-item-${tx.id}`}
                    >
                      <div className="flex items-center gap-4">
                        {getStatusIcon(tx.payment_status)}
                        <div>
                          <p className="text-white text-sm font-medium">
                            {tx.plan_name} - {tx.billing_cycle === 'yearly' ? 'Annual' : 'Monthly'}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {formatDate(tx.created_at)}
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-white font-medium">
                          ${tx.amount?.toFixed(2) || '0.00'}
                        </p>
                        <p className={`text-xs capitalize ${getStatusColor(tx.payment_status)}`}>
                          {tx.payment_status}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <Receipt size={32} className="text-muted-foreground mx-auto mb-3" />
                  <p className="text-muted-foreground">No billing history yet</p>
                  {usage?.tier === 'free' && (
                    <Link to="/pricing">
                      <Button className="btn-primary mt-4">
                        Upgrade to get started
                      </Button>
                    </Link>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {/* App Settings Section */}
        <div className="luxury-card p-6 mt-6">
          {FEATURES.TOKENISATION_ENABLED && (
            <>
              {/* Wallet & Balance */}
              <div className="flex items-center gap-3 mb-6">
                <Wallet size={20} className="text-gold" />
                <h2 className="font-serif text-xl text-white">Wallet</h2>
              </div>
              <div className="p-6 bg-[#0A0A0A] border border-[#1A1A1A] mb-6" data-testid="wallet-section">
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                  <div>
                    <p className="text-xs text-muted-foreground uppercase tracking-wider">Available Balance</p>
                    <p className="text-3xl font-serif text-gold">{formatCurrency(user?.wallet_balance || 0)}</p>
                    <p className="text-xs text-muted-foreground mt-1">
                      {connectStatus?.payouts_enabled
                        ? 'Future fraction sales settle directly to your Stripe account (Express).'
                        : 'Credited from sales that occurred before you enabled Stripe payouts.'}
                    </p>
                  </div>
                  <div className="text-right">
                    {connectStatus?.payouts_enabled && (
                      <span className="inline-flex items-center gap-1 px-2 py-1 bg-emerald-500/10 border border-emerald-500/30 rounded text-xs text-emerald-400">
                        <CheckCircle size={12} /> Payouts active
                      </span>
                    )}
                  </div>
                </div>
              </div>

              {/* Stripe Connect Payouts */}
              <div className="p-6 bg-[#0A0A0A] border border-[#1A1A1A] mb-10" data-testid="stripe-connect-section">
                <div className="flex items-center gap-3 mb-3">
                  <Banknote size={18} className="text-gold" />
                  <h3 className="text-white text-sm font-medium">Seller Payouts - Stripe Connect</h3>
                </div>

                {!connectStatus ? (
                  <div className="flex items-center gap-2 text-xs text-muted-foreground"><Loader2 size={12} className="animate-spin" /> Checking status...</div>
                ) : !connectStatus.connected ? (
                  <div>
                    <p className="text-xs text-muted-foreground leading-relaxed mb-4">
                      To list Digital Twin fractions for sale you must enable payouts. We route buyer payments to your
                      bank via Stripe Express. Takes 3-5 minutes - you'll need ID and bank details.
                    </p>
                    <Button onClick={handleConnectOnboarding} disabled={connectLoading} className="btn-primary flex items-center gap-2" data-testid="setup-payouts-btn">
                      {connectLoading ? <Loader2 size={14} className="animate-spin" /> : <Banknote size={14} />}
                      Set up payouts
                    </Button>
                  </div>
                ) : connectStatus.payouts_enabled ? (
                  <div>
                    <div className="flex items-center gap-2 mb-3">
                      <span className="inline-flex items-center gap-1 px-2 py-1 bg-emerald-500/10 border border-emerald-500/30 rounded text-xs text-emerald-400">
                        <CheckCircle size={12} /> Connected & verified
                      </span>
                      <span className="text-[10px] text-muted-foreground font-mono">{connectStatus.account_id}</span>
                    </div>
                    <p className="text-xs text-muted-foreground leading-relaxed mb-4">
                      Sales of your fractions are transferred to your Stripe balance automatically (minus the 1.5% platform fee).
                      Visit the Stripe dashboard to view payouts, update bank details or download tax documents.
                    </p>
                    <Button onClick={handleOpenDashboard} disabled={connectLoading} variant="outline" className="border-gold/30 text-gold hover:bg-gold/10 flex items-center gap-2" data-testid="open-stripe-dashboard-btn">
                      {connectLoading ? <Loader2 size={14} className="animate-spin" /> : <ExternalLink size={14} />}
                      Open Stripe Dashboard
                    </Button>
                  </div>
                ) : (
                  <div>
                    <div className="flex items-center gap-2 mb-3">
                      <span className="inline-flex items-center gap-1 px-2 py-1 bg-amber-500/10 border border-amber-500/30 rounded text-xs text-amber-400">
                        <AlertCircle size={12} /> Action required
                      </span>
                    </div>
                    <p className="text-xs text-muted-foreground leading-relaxed mb-3">
                      Stripe still needs more information to activate your payouts. Complete onboarding to enable selling.
                    </p>
                    {connectStatus.requirements_currently_due?.length > 0 && (
                      <p className="text-[10px] text-muted-foreground mb-3">
                        Required: <span className="text-amber-400">{connectStatus.requirements_currently_due.slice(0, 4).join(', ')}</span>
                      </p>
                    )}
                    <Button onClick={handleConnectOnboarding} disabled={connectLoading} className="btn-primary flex items-center gap-2" data-testid="complete-payouts-btn">
                      {connectLoading ? <Loader2 size={14} className="animate-spin" /> : <ArrowRight size={14} />}
                      Complete onboarding
                    </Button>
                  </div>
                )}
              </div>
            </>
          )}

          <div className="flex items-center gap-3 mb-6">
            <Crown size={20} className="text-gold" />
            <h2 className="font-serif text-xl text-white">App Settings</h2>
          </div>

          <div className="space-y-4">
            {/* Currency Selector */}
            <div className="relative">
              <button
                onClick={() => setShowCurrencyDropdown(!showCurrencyDropdown)}
                className="w-full flex items-center justify-between p-4 bg-[#0A0A0A] border border-[#1A1A1A] hover:border-gold/30 transition-colors"
                data-testid="currency-selector"
              >
                <div className="flex items-center gap-4">
                  <Coins size={20} className="text-gold" />
                  <div className="text-left">
                    <p className="text-white text-sm font-medium">Currency</p>
                    <p className="text-xs text-muted-foreground">
                      {currencyConfig[currency]?.name || currency} ({currencyConfig[currency]?.symbol})
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-gold font-medium">{currency}</span>
                  {showCurrencyDropdown ? (
                    <ChevronUp size={16} className="text-muted-foreground" />
                  ) : (
                    <ChevronDown size={16} className="text-muted-foreground" />
                  )}
                </div>
              </button>

              {showCurrencyDropdown && (
                <div className="absolute top-full left-0 right-0 mt-1 bg-[#0A0A0A] border border-[#1A1A1A] z-50">
                  {currencies.map((curr) => (
                    <button
                      key={curr}
                      onClick={async () => {
                        const success = await updateCurrency(curr);
                        if (success) {
                          toast.success(`Currency changed to ${currencyConfig[curr]?.name}`);
                          setShowCurrencyDropdown(false);
                        } else {
                          toast.error('Failed to update currency');
                        }
                      }}
                      disabled={currencyLoading}
                      className={`w-full flex items-center justify-between p-4 hover:bg-[#1A1A1A] transition-colors ${
                        currency === curr ? 'bg-gold/10 border-l-2 border-gold' : ''
                      }`}
                      data-testid={`currency-option-${curr}`}
                    >
                      <div className="flex items-center gap-3">
                        <span className="text-lg">{currencyConfig[curr]?.symbol}</span>
                        <div className="text-left">
                          <p className="text-white text-sm">{currencyConfig[curr]?.name}</p>
                          <p className="text-xs text-muted-foreground">{curr}</p>
                        </div>
                      </div>
                      {currency === curr && (
                        <CheckCircle size={16} className="text-gold" />
                      )}
                    </button>
                  ))}
                  <div className="p-3 border-t border-[#1A1A1A]">
                    <p className="text-xs text-muted-foreground text-center">
                      Live exchange rates • Updated hourly
                    </p>
                  </div>
                </div>
              )}
            </div>

            {/* Notifications Toggle */}
            <div className="flex items-center justify-between p-4 bg-[#0A0A0A] border border-[#1A1A1A]">
              <div className="flex items-center gap-4">
                {notificationPermission === 'granted' ? (
                  <Bell size={20} className="text-gold" />
                ) : (
                  <BellOff size={20} className="text-muted-foreground" />
                )}
                <div>
                  <p className="text-white text-sm font-medium">Push Notifications</p>
                  <p className="text-xs text-muted-foreground">
                    {notificationPermission === 'granted' 
                      ? 'Notifications enabled' 
                      : notificationPermission === 'denied'
                      ? 'Notifications blocked'
                      : 'Get alerts for price changes'}
                  </p>
                </div>
              </div>
              {notificationPermission !== 'denied' && (
                <button
                  onClick={async () => {
                    const granted = await requestNotificationPermission();
                    if (granted) {
                      toast.success('Notifications enabled');
                    }
                  }}
                  disabled={notificationPermission === 'granted'}
                  className={`relative w-12 h-6 rounded-full transition-colors ${
                    notificationPermission === 'granted' ? 'bg-gold' : 'bg-[#1A1A1A]'
                  }`}
                  data-testid="notification-toggle"
                >
                  <span className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-transform ${
                    notificationPermission === 'granted' ? 'left-7' : 'left-1'
                  }`} />
                </button>
              )}
            </div>

            {/* Share Portfolio */}
            <button
              onClick={async () => {
                try {
                  if (shareContent) {
                    const shared = await shareContent({
                      title: 'Assagart - My Portfolio',
                      text: 'Check out my luxury asset portfolio on Assagart!',
                      url: window.location.origin
                    });
                    if (shared) {
                      toast.success('Shared successfully');
                      return;
                    }
                  }
                  // Fallback to clipboard
                  await navigator.clipboard.writeText(window.location.origin);
                  toast.success('Link copied to clipboard');
                } catch (error) {
                  console.error('Share error:', error);
                  // Final fallback
                  try {
                    await navigator.clipboard.writeText(window.location.origin);
                    toast.success('Link copied to clipboard');
                  } catch (e) {
                    toast.error('Unable to share');
                  }
                }
              }}
              className="w-full flex items-center justify-between p-4 bg-[#0A0A0A] border border-[#1A1A1A] hover:border-gold/30 transition-colors"
              data-testid="share-portfolio-btn"
            >
              <div className="flex items-center gap-4">
                <Share2 size={20} className="text-muted-foreground" />
                <div className="text-left">
                  <p className="text-white text-sm font-medium">Share Portfolio</p>
                  <p className="text-xs text-muted-foreground">Invite others to Assagart</p>
                </div>
              </div>
              <ArrowRight size={16} className="text-muted-foreground" />
            </button>
          </div>
        </div>

        {/* Security Settings Section */}
        <div className="mt-6">
          <SecuritySettings />
        </div>

        {/* Legal & Policies Section */}
        <div className="luxury-card p-8 mt-6" data-testid="legal-section">
          <div className="flex items-center gap-3 mb-6">
            <Scale size={24} className="text-gold" />
            <h2 className="font-serif text-xl text-white">Legal & Policies</h2>
          </div>

          <p className="text-muted-foreground text-sm mb-4">
            Review our policies and legal information to understand how we operate and protect your interests.
          </p>

          <div className="space-y-2">
            <Link
              to="/privacy"
              className="w-full flex items-center justify-between p-4 bg-[#0A0A0A] border border-[#1A1A1A] hover:border-gold/30 transition-colors"
              data-testid="privacy-link"
            >
              <div className="flex items-center gap-4">
                <FileText size={20} className="text-muted-foreground" />
                <div className="text-left">
                  <p className="text-white text-sm font-medium">Privacy Policy</p>
                  <p className="text-xs text-muted-foreground">How we handle your data, encrypted PII, and GDPR rights</p>
                </div>
              </div>
              <ArrowRight size={16} className="text-muted-foreground" />
            </Link>

            <Link
              to="/terms"
              className="w-full flex items-center justify-between p-4 bg-[#0A0A0A] border border-[#1A1A1A] hover:border-gold/30 transition-colors"
              data-testid="terms-link"
            >
              <div className="flex items-center gap-4">
                <FileText size={20} className="text-muted-foreground" />
                <div className="text-left">
                  <p className="text-white text-sm font-medium">Terms of Service</p>
                  <p className="text-xs text-muted-foreground">Usage terms, payments, and user responsibilities</p>
                </div>
              </div>
              <ArrowRight size={16} className="text-muted-foreground" />
            </Link>

            <Link
              to="/cookies"
              className="w-full flex items-center justify-between p-4 bg-[#0A0A0A] border border-[#1A1A1A] hover:border-gold/30 transition-colors"
              data-testid="cookies-link"
            >
              <div className="flex items-center gap-4">
                <Cookie size={20} className="text-muted-foreground" />
                <div className="text-left">
                  <p className="text-white text-sm font-medium">Cookie Policy</p>
                  <p className="text-xs text-muted-foreground">How we use cookies and local storage</p>
                </div>
              </div>
              <ArrowRight size={16} className="text-muted-foreground" />
            </Link>

            <Link
              to="/disclaimer"
              className="w-full flex items-center justify-between p-4 bg-[#0A0A0A] border border-[#1A1A1A] hover:border-gold/30 transition-colors"
              data-testid="disclaimer-link"
            >
              <div className="flex items-center gap-4">
                <AlertCircle size={20} className="text-muted-foreground" />
                <div className="text-left">
                  <p className="text-white text-sm font-medium">Disclaimer</p>
                  <p className="text-xs text-muted-foreground">AI valuations, investment risks, and limitations</p>
                </div>
              </div>
              <ArrowRight size={16} className="text-muted-foreground" />
            </Link>
          </div>

          <div className="mt-6 p-4 bg-[#0A0A0A] border border-[#1A1A1A] rounded">
            <p className="text-xs text-muted-foreground">
              By using Assagart, you agree to our Terms of Service and acknowledge our use of cookies. 
              AI valuations are estimates only and should not be used as financial advice.
            </p>
          </div>
        </div>

        {/* Cancel Membership Dialog */}
        <Dialog open={cancelDialog} onOpenChange={(open) => !processing && setCancelDialog(open)}>
          <DialogContent className="bg-[#0F0F0F] border-[#262626]">
            <DialogHeader>
              <DialogTitle className="font-serif text-xl text-white flex items-center gap-2">
                <AlertTriangle size={24} className="text-red-500" />
                Cancel Membership
              </DialogTitle>
              <DialogDescription className="text-muted-foreground">
                Are you sure you want to cancel your membership?
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4">
              <div className="p-4 bg-amber-500/10 border border-amber-500/30 rounded">
                <p className="text-sm text-amber-300 font-medium">
                  Here's what happens when you cancel:
                </p>
                <ul className="text-sm text-amber-200/90 mt-2 space-y-1">
                  <li>• You'll keep full access until{' '}
                    <strong>
                      {subscriptionDetails?.next_billing_date
                        ? new Date(subscriptionDetails.next_billing_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })
                        : 'the end of your current billing period'}
                    </strong>
                  </li>
                  <li>• No further charges after that date</li>
                  <li>• You'll be moved to the free Starter plan automatically</li>
                  <li>• Your assets stay safe — just limited to 5 visible</li>
                  <li>• You can reactivate anytime before the cancellation date</li>
                </ul>
              </div>

              <div className="flex gap-3">
                <Button 
                  onClick={handleCancelSubscription}
                  disabled={processing}
                  className="flex-1 bg-red-600 hover:bg-red-700 text-white"
                  data-testid="confirm-cancel-btn"
                >
                  {processing ? (
                    <>
                      <Loader2 size={16} className="mr-2 animate-spin" />
                      Cancelling...
                    </>
                  ) : (
                    'Yes, Cancel Membership'
                  )}
                </Button>
                <Button 
                  onClick={() => setCancelDialog(false)}
                  disabled={processing}
                  className="btn-secondary"
                >
                  Keep Membership
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Help & Contact */}
        <div className="luxury-card p-6" data-testid="help-contact-section">
          <h2 className="font-serif text-lg text-white mb-4 flex items-center gap-2">
            <HelpCircle size={18} className="text-gold" />
            Help & Contact
          </h2>
          <p className="text-sm text-muted-foreground mb-4">
            Having trouble or need assistance? Our team is here to help.
          </p>
          <div className="space-y-3">
            <button 
              onClick={() => {
                navigator.clipboard.writeText('support@assagart.co.uk');
                toast.success('Email address copied to clipboard');
              }}
              className="flex items-center gap-3 p-3 bg-[#0A0A0A] border border-[#1A1A1A] hover:border-gold/30 transition-colors w-full text-left"
              data-testid="help-email-link"
            >
              <Mail size={16} className="text-gold" />
              <div className="flex-1">
                <p className="text-sm text-white">support@assagart.co.uk</p>
                <p className="text-xs text-muted-foreground">General enquiries and account support</p>
              </div>
              <Copy size={14} className="text-muted-foreground" />
            </button>
          </div>
        </div>

        {/* Downgrade Dialog */}
        <Dialog open={downgradeDialog} onOpenChange={(open) => !processing && setDowngradeDialog(open)}>
          <DialogContent className="bg-[#0F0F0F] border-[#262626]">
            <DialogHeader>
              <DialogTitle className="font-serif text-xl text-white">
                Downgrade to Professional
              </DialogTitle>
              <DialogDescription className="text-muted-foreground">
                Switch from Elite to Professional plan
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4">
              <div className="p-4 bg-[#0A0A0A] border border-[#1A1A1A]">
                <div className="flex justify-between mb-2">
                  <span className="text-muted-foreground">New Plan</span>
                  <span className="text-blue-400">Professional</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Asset Limit</span>
                  <span className="text-white">25 assets</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Price</span>
                  <span className="text-white">£29/month</span>
                </div>
              </div>

              <p className="text-xs text-muted-foreground">
                Note: If you have more than 25 assets, you won't be able to add new assets until you're under the limit.
              </p>

              <div className="flex gap-3">
                <Button 
                  onClick={handleDowngrade}
                  disabled={processing}
                  className="btn-primary flex-1"
                  data-testid="confirm-downgrade-btn"
                >
                  {processing ? (
                    <>
                      <Loader2 size={16} className="mr-2 animate-spin" />
                      Processing...
                    </>
                  ) : (
                    'Confirm Downgrade'
                  )}
                </Button>
                <Button 
                  onClick={() => setDowngradeDialog(false)}
                  disabled={processing}
                  className="btn-secondary"
                >
                  Keep Elite
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
